import { Pause, Play, Sun, Sunrise, Sunset } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SunCalc from 'suncalc';

import { useOptionalMapLibre } from './MapLibreContext';
import { useSunlight } from './SunlightContext';

interface SunlightControlProps { initialDate?: Date }

function clamp(min: number, val: number, max: number) { return Math.max(min, Math.min(val, max)); }

export const SunlightControl: React.FC<SunlightControlProps> = ({ initialDate }) => {
  const map = useOptionalMapLibre();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const { sunDate, setSunDate } = useSunlight();
  const handleClose = () => setIsOpen(false);
  const base = initialDate ?? sunDate;
  const currentYear = base.getFullYear();
  const [month, setMonth] = useState<number>(base.getMonth() + 1);
  const [day, setDay] = useState<number>(base.getDate());
  const [hour, setHour] = useState<number>(base.getHours());
  const initialMinute = Math.floor(base.getMinutes() / 15) * 15;
  const [minute, setMinute] = useState<number>(initialMinute);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const STEP_MINUTES = 15;
  const absMinutesRef = useRef<number>((hour * 60) + minute);

  const [centerLatLng, setCenterLatLng] = useState<{ lat: number; lng: number }>(() => {
    const c = map?.getCenter();
    return { lat: c?.lat ?? 0, lng: c?.lng ?? 0 };
  });
  useEffect(() => {
    if (!map) { return; }
    const update = () => { const c = map.getCenter(); setCenterLatLng({ lat: c.lat, lng: c.lng }); };
    update(); map.on('moveend', update); return () => { map.off('moveend', update); };
  }, [map]);
  const lat = centerLatLng.lat; const lng = centerLatLng.lng;

  const computedDate = useMemo(() => {
    const daysInMonth = new Date(currentYear, month, 0).getDate();
    const safeDay = Math.min(day, daysInMonth);
    return new Date(currentYear, month - 1, safeDay, hour, minute, 0, 0);
  }, [currentYear, month, day, hour, minute]);

  const times = useMemo(() => SunCalc.getTimes(computedDate, lat, lng), [computedDate, lat, lng]);
  const minHour = useMemo(() => Math.max(0, Math.min(23, (times.sunrise?.getHours?.() ?? 6))), [times]);
  const maxHour = useMemo(() => Math.max(minHour, Math.min(23, (times.sunset?.getHours?.() ?? 18))), [times, minHour]);
  const sunriseMinutes = useMemo(() => { const t = times.sunrise ?? new Date(currentYear, month - 1, day, 6, 0, 0, 0); return (t.getHours() * 60) + t.getMinutes(); }, [times, currentYear, month, day]);
  const sunsetMinutes = useMemo(() => { const t = times.sunset ?? new Date(currentYear, month - 1, day, 18, 0, 0, 0); return (t.getHours() * 60) + t.getMinutes(); }, [times, currentYear, month, day]);

  const sun = useMemo(() => {
    const pos = SunCalc.getPosition(computedDate, lat, lng);
    return { azimuth: pos.azimuth, altitude: pos.altitude, date: computedDate };
  }, [computedDate, lat, lng]);

  const noonAltitude = useMemo(() => {
    const d = times.solarNoon ?? new Date(currentYear, month - 1, day, 12, 0, 0, 0);
    const p = SunCalc.getPosition(d, lat, lng);
    return Math.max(0, p.altitude);
  }, [times, currentYear, month, day, lat, lng]);

  useEffect(() => {
    const azimuthFromSouthDeg = (sun.azimuth * 180) / Math.PI;
    const azimuthFromNorthDeg = (azimuthFromSouthDeg + 180) % 360;
    const altitudeDeg = (sun.altitude * 180) / Math.PI;
    if (!map) { return; }
    const anchor = 'map' as const; const r = 1.15; const intensity = clamp(0.1, (altitudeDeg + 5) / 60, 1);
    map.setLight({ anchor, color: '#ffffff', intensity, position: [r, azimuthFromNorthDeg, Math.max(0, altitudeDeg)] } as unknown as maplibregl.LightSpecification);
  }, [map, sun.azimuth, sun.altitude]);

  useEffect(() => { if (hour < minHour) { setHour(minHour); return; } if (hour > maxHour) { setHour(maxHour); return; } }, [minHour, maxHour]);
  useEffect(() => { setSunDate(new Date(computedDate)); }, [computedDate, setSunDate]);
  useEffect(() => { absMinutesRef.current = (hour * 60) + minute; }, [hour, minute]);
  useEffect(() => { if (isPlaying && sunriseMinutes >= sunsetMinutes) { setIsPlaying(false); } }, [isPlaying, sunriseMinutes, sunsetMinutes]);
  useEffect(() => {
    if (!isOpen) { return; }
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') { setIsOpen(false); } };
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) { return; }
      if (panelRef.current?.contains(t)) { return; }
      if (triggerRef.current?.contains(t)) { return; }
      setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => { document.removeEventListener('keydown', onKeyDown); document.removeEventListener('mousedown', onMouseDown); };
  }, [isOpen]);
  useEffect(() => {
    if (!isPlaying) { return; }
    const id = setInterval(() => {
      const start = sunriseMinutes; const end = sunsetMinutes; const startStep = Math.ceil(start / STEP_MINUTES) * STEP_MINUTES; const endStep = Math.floor(end / STEP_MINUTES) * STEP_MINUTES; if (endStep <= startStep) { return; }
      let currentAbs = absMinutesRef.current; if (currentAbs < startStep || currentAbs > endStep) { currentAbs = startStep; }
      let nextAbs = currentAbs + STEP_MINUTES; if (nextAbs > endStep) { nextAbs = startStep; }
      absMinutesRef.current = nextAbs; const nextHour = Math.floor(nextAbs / 60); const nextMinute = nextAbs % 60; setHour(nextHour); setMinute(nextMinute);
    }, 100);
    return () => clearInterval(id);
  }, [isPlaying, sunriseMinutes, sunsetMinutes]);

  const progress = useMemo(() => {
    const current = (hour * 60) + minute; const start = sunriseMinutes; const end = Math.max(start + 1, sunsetMinutes); const clamped = clamp(0, current - start, end - start); return clamp(0, clamped / (end - start), 1);
  }, [hour, minute, sunriseMinutes, sunsetMinutes]);

  return (
    <div id="sunlight-target" className="absolute right-4 bottom-4 z-[430]">
      <div 
        className="flex items-center space-x-2 rounded-full bg-white p-2 shadow transition-all duration-200 hover:shadow-lg"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span className="relative inline-block align-middle">
          <button
            className={`rounded-full p-2 transition-all duration-200 ${
              isHovered 
                ? 'bg-orange-400 text-white shadow-lg hover:bg-orange-500' 
                : 'bg-yellow-400 text-white hover:bg-yellow-500'
            }`}
            title="Ensoleillement"
            onClick={() => setIsOpen((p) => !p)}
            aria-expanded={isOpen}
            aria-controls="sunlight-panel"
            ref={triggerRef}
          >
            <Sun className={`h-6 w-6 transition-all duration-200 ${ isHovered ? 'text-yellow-100 drop-shadow-sm' : '' }`} />
          </button>
        </span>
        <div className={`text-sm font-medium transition-colors duration-200 select-none ${ isHovered ? 'text-gray-900' : 'text-gray-700' }`}>
          {String(hour).padStart(2, '0')}:{String(minute).padStart(2, '0')} · {String(day).padStart(2, '0')}/{String(month).padStart(2, '0')}
        </div>
        {isOpen && (
          <div
            ref={panelRef}
            id="sunlight-panel"
            role="dialog"
            aria-modal="false"
            className="absolute right-0 bottom-0"
          >
            <div className="w-[360px] max-w-[90vw] overflow-hidden rounded-xl bg-white p-0 shadow">
              <div className="bg-gradient-to-b from-sky-200 via-blue-200 to-white p-4">
                <div className="mb-1 font-semibold">Ensoleillement</div>
                <div className="mb-2 text-xs text-gray-700">{computedDate.toLocaleDateString('fr-FR')}</div>
                <div className="relative h-24 w-full rounded-lg bg-gradient-to-r from-indigo-900 via-sky-300 to-orange-200">
                  <div className="absolute bottom-2 left-3 flex items-center text-xs text-white"><Sunrise className="mr-1 h-4 w-4"/>Lever</div>
                  <div className="absolute right-3 bottom-2 flex items-center text-xs text-white"><Sunset className="mr-1 h-4 w-4"/>Coucher</div>
                  <div className="absolute inset-0">
                    <div className="relative h-full w-full">
                      <Sun className="absolute h-8 w-8 cursor-pointer text-yellow-300 drop-shadow transition-colors duration-300 hover:text-orange-300"
                        style={{ left: `calc(${(progress * 100).toFixed(2)}% - 16px)`, top: `calc(${(100 - (Math.max(0, Math.min(1, noonAltitude > 0 ? (sun.altitude / noonAltitude) : 0)) * 100)).toFixed(2)}% - 16px)` }} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-3 p-4">
                <div className="text-xs text-gray-600">Sélectionnez le jour et le mois</div>
                <div className="grid grid-cols-3 items-center gap-2">
                  <select className="rounded border px-2 py-1 text-sm transition-colors duration-200 hover:border-blue-300 focus:border-blue-500 focus:outline-none" value={day} onChange={(e) => setDay(Number(e.target.value))} aria-label="Jour" title="Jour">
                    {Array.from({ length: new Date(currentYear, month, 0).getDate() }, (_, i) => i + 1).map(d => (<option key={d} value={d}>{String(d).padStart(2, '0')}</option>))}
                  </select>
                  <select className="col-span-2 rounded border px-2 py-1 text-sm transition-colors duration-200 hover:border-blue-300 focus:border-blue-500 focus:outline-none" value={month} onChange={(e) => setMonth(Number(e.target.value))} aria-label="Mois" title="Mois">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (<option key={m} value={m}>{String(m).padStart(2, '0')}</option>))}
                  </select>
                </div>
                <div className="grid grid-cols-1 items-center gap-2">
                  <input type="range" min={minHour} max={maxHour} step={1} value={hour} onChange={(e) => { setIsPlaying(false); setHour(Number(e.target.value)); }} className="hover:cursor-pointer" />
                  <div className="text-sm text-gray-700">Heure : {String(hour).padStart(2, '0')}:{String(minute).padStart(2, '0')} (Lever {String(minHour).padStart(2, '0')}h · Coucher {String(maxHour).padStart(2, '0')}h)</div>
                  <div className="flex items-center justify-between border-t border-gray-200 pt-2">
                    <div className="mb-1 text-sm text-gray-700">Cycle de journée automatique</div>
                    <button className={`inline-flex items-center justify-center rounded-full text-white shadow ${isPlaying ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'} h-9 w-9 transition-colors duration-200`} onClick={() => setIsPlaying((p) => !p)} aria-pressed={isPlaying} aria-label={isPlaying ? 'Mettre en pause la lecture automatique' : 'Lancer la lecture automatique'} title={isPlaying ? 'Pause' : 'Lecture'}>
                      {isPlaying ? <Pause className="h-4 w-4"/> : <Play className="h-4 w-4"/>}
                    </button>
                  </div>
                </div>
                <div className="text-xs text-gray-500">Azimuth : {(sun.azimuth * 180 / Math.PI).toFixed(1)}° · Altitude : {(sun.altitude * 180 / Math.PI).toFixed(1)}°</div>
                <div className="flex justify-end"><button className="rounded bg-gray-200 px-3 py-1 text-gray-800 transition-colors duration-200 hover:bg-gray-300" onClick={handleClose}>Fermer</button></div>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};
